/**
 * Portfolio component
 *
 * Highlights some of  your creations. These can be designs, websites,
 * open source contributions, articles you've written and more.
 *
 * This is a great area for you to to continually add to and refine
 * as you continue to learn and create.
 */

import React from "react";

/**
 * Desk image
 *
 * Below is a sample desk image. Feel free to update this to an image of your choice,
 * updating below imageAltText to string that represents what you see in that image.
 *
 * Need an image? Check out https://unsplash.com to download a photo you
 * freely use on your site.
 */
import image from "url:../images/design-desk.jpeg";

const imageAltText = "desktop with books and laptop";

/**
 * Project list
 *
 * An array of objects that will be used to display for your project
 * links section. Below is a sample, update to reflect links you'd like to highlight.
 */
const projectList = [
  {
    title: "FilmHub",
    dateRange: "November 2024 - June 2025 (8 months)",
    description:
      "Developed a system utilizing The Movie Database (TMDb) API to provide users with information on popular TV shows and movies. Features allow users to view detailed information about movies and TV shows, including cast, crew, and trailers.",
    technologies: ["Flutter", "Dart", "Android", "Web", "Clean Architecture"],
    actions: [
      { url: "https://noone-m.github.io/filmhub", label: "Go to FilmHub" },
      { url: "https://github.com/noone-m/filmhub", label: "Source Code" },
    ],
  },
  {
    title: "BlobsWar",
    dateRange: "December 2024 - February 2025 (2 months)",
    description:
      "University project: Developed a desktop game supporting both single-player and two-player modes. The game leverages advanced AI search algorithms to provide a challenging experience.",
    technologies: ["Python", "Pygame", "Desktop", "AI Search Algorithms"],
    url: "https://drive.google.com/file/d/18jl09zCJ-SEcVN7QnGafic7Uh56EcMiR/view",
    actionLabel: "Download Game",
  },
  {
    title: "FastAPI Secure File Upload",
    dateRange: "October 2025 - December 2025 (3 months)",
    description:
      "A production-ready, asynchronous file management system featuring zero-trust security. Implements real MIME detection via libmagic, atomic file writes, and protection against path traversal and DoS attacks.",
    technologies: ["FastAPI", "Python", "Asynchronous Programming", "aiofiles", "Cybersecurity"],
    url: "https://github.com/noone-m/fastapi-secure-file-upload",
    actionLabel: "View Repository",
  },
  {
    title: "Meddit: AI Medical Assistant & Platform",
    dateRange: "2025 - Present",
    description:
          "A comprehensive healthcare platform developed as part of the winning project at the 'Hack for Syria' Hackathon (2025). The system features an AI-powered symptom checker, a real-time chat system with message delivery tracking, and a secure multi-role (Patient/Doctor/Admin) authentication flow with OTP verification.",
    technologies: ["AI Integration", "Real-time Chat", "JWT/OTP", "User Journey Design", "System Architecture"],
    url: "https://noone-m.github.io/2026-08-27-meddit-project-case-study/",
    actionLabel: "View Project Case Study",
  },
  {
    title: "WajeezAI",
    subtitle: "(وجيز)",
    dateRange: "October 2025 - Present",
    description:
      "An AI-powered application that summarizes lectures by converting the professor's voice into text, extracting content from lecture images, and generating concise, structured summaries for students.",
    technologies: ["PyTorch", "FastAPI", "Flutter", "Speech to Text", "OCR", "Prompt Engineering", "NLP"],
    url: "https://noone-m.github.io/2026-08-27-wajeezai-project-case-study/",
    actionLabel: "View Project Case Study And Demo",
  },
  {
    title: "Enterprise Network Design",
    dateRange: "2024",
    description:
      "Designed a robust, multi-site network infrastructure for a company with three geographical branches. Implemented VLANs, VTP, Inter-VLAN routing, and SSH-based remote management, secured via Port Security and ACLs.",
    technologies: ["Cisco Packet Tracer", "Networking", "VLAN/VTP", "Routing & Switching", "Network Security"],
    url: "https://github.com/noone-m/company-network-design.",
    actionLabel: "View Repository",
  },
  {
    title: "Nurikabe Solver",
    dateRange: "2024",
    description:
      "A logic-based puzzle solver written in Prolog. Utilizes 13 distinct solving techniques to navigate complex grid constraints, featuring an extensible architecture for advanced island and stream logic.",
    technologies: ["Prolog", "Logic Programming", "Artificial Intelligence", "Constraint Solving"],
    url: "https://github.com/noone-m/Nurikabe",
    actionLabel: "View Repository",
  }
];

const Portfolio = () => {
  return (
    <section className="padding portfolio-section" id="portfolio">
      <div className="portfolio-header">
        <h2>Selected Work</h2>
        <p>
          A blend of product engineering, AI integration, and system design focused on real-world
          outcomes.
        </p>
      </div>
      <div className="portfolio-layout">
        <div className="portfolio-image-wrap">
          <img src={image} className="portfolio-image" alt={imageAltText} />
        </div>
        <div className="project-grid">
          {projectList.map((project) => (
            <article className="project-card" key={project.title}>
              <div className="project-header">
                <div>
                  <h3>{project.title}</h3>
                  {project.subtitle && <span className="project-subtitle">{project.subtitle}</span>}
                </div>
              </div>
              <p className="project-date">{project.dateRange}</p>
              <p className="small">{project.description}</p>
              <div className="technologies-container">
                {project.technologies.map((tech) => (
                  <span className="tech-badge" key={tech}>
                    {tech}
                  </span>
                ))}
              </div>
              {project.actions ? (
                project.actions.map(
                  (action) =>
                    action.label && action.url && (
                      <a
                        key={`${project.title}-${action.label}`}
                        href={action.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-action-button"
                      >
                        {action.label}
                      </a>
                    )
                )
              ) : project.actionLabel && project.url ? (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-action-button"
                >
                  {project.actionLabel}
                </a>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
